import { AlertTriangle, ArrowRight, RotateCw } from "lucide-react";

import { Button } from "@/components/Button";
import type { IngestionSession } from "@/api/types";

/**
 * What to say when a source was missing (§8).
 *
 * The distinction this panel exists to draw: **a model being down is not an
 * analysis failure.** The photo is still there, the barcode and the text were
 * read, matching ran. What is on screen is usable and incomplete, and the
 * operator's next move is either "continue with this" or "I just switched the
 * Mac mini on, try again" — two buttons, not an error.
 *
 * Nothing technical reaches the screen. No `ECONNREFUSED`, no host, no port, no
 * stack trace: a technician standing in a warehouse cannot act on any of it,
 * and the server deliberately does not send it. What they get is one sentence
 * about what happened and what still works.
 */

/** The sentences come from the server, which knows *why*. This is the fallback. */
const DEFAULT_NOTICE = "Vision locale indisponible.";

export function DegradedNotice({
  session,
  onRetryVision,
  onContinue,
  retrying,
}: {
  session: IngestionSession;
  onRetryVision: () => void;
  onContinue?: () => void;
  retrying?: boolean;
}) {
  const analysis = (session.metadata_json?.analysis ?? {}) as {
    degraded?: boolean;
    local_status?: string;
    local_vision_status?: string;
  };

  if (!analysis.degraded) return null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning-subtle px-3 py-2.5">
      <p className="flex items-start gap-2 text-[13px] font-medium text-warning">
        <AlertTriangle className="mt-px size-4 shrink-0" aria-hidden />
        Analyse partielle
      </p>
      <ul className="flex flex-col gap-0.5 text-[12px] text-ink-muted">
        <li>✓ Code-barres et texte de l'étiquette lus</li>
        <li>⚠ {analysis.local_status || DEFAULT_NOTICE}</li>
      </ul>
      <p className="text-[12px] text-ink-muted">
        HD-Vault peut continuer avec les informations disponibles.
      </p>
      <div className="flex flex-wrap gap-2 pt-0.5">
        <Button size="sm" variant="secondary" onClick={onRetryVision} loading={retrying}>
          <RotateCw className="size-3.5" aria-hidden />
          Réessayer la vision
        </Button>
        {onContinue && (
          <Button size="sm" variant="ghost" onClick={onContinue}>
            Continuer
            <ArrowRight className="size-3.5" aria-hidden />
          </Button>
        )}
      </div>
    </div>
  );
}
